import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { getOrdenesCompra, deleteOrdenCompra } from '../services/api';
import api from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ClipboardList, Plus, Eye, Pencil, Trash2, Download, FileText, Receipt } from 'lucide-react';

const ESTADO_COLORS = {
  PENDIENTE: 'bg-yellow-100 text-yellow-700',
  APROBADA: 'bg-blue-100 text-blue-700',
  ENVIADA: 'bg-purple-100 text-purple-700',
  FACTURADA: 'bg-emerald-100 text-emerald-700',
  COMPLETADA: 'bg-emerald-100 text-emerald-700',
  CANCELADA: 'bg-red-100 text-red-700'
};

const OrdenesCompraPage = () => {
  const navigate = useNavigate();
  const { empresaActual } = useApp();
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState('all');
  const [filtroNumero, setFiltroNumero] = useState('');
  const [filtroProveedor, setFiltroProveedor] = useState('');
  
  // Modal eliminar
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [ordenToDelete, setOrdenToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Modal crear factura
  const [facturaOpen, setFacturaOpen] = useState(false);
  const [ordenToFactura, setOrdenToFactura] = useState(null);
  const [facturaData, setFacturaData] = useState({ fecha: new Date().toISOString().split('T')[0], serie: '', nro: '', tc: 1 });
  const [creatingFactura, setCreatingFactura] = useState(false);

  const loadData = useCallback(async () => {
    if (!empresaActual?.id) return;
    setLoading(true);
    try {
      const res = await getOrdenesCompra(empresaActual.id, filtroEstado === 'all' ? undefined : filtroEstado);
      setOrdenes(res.data);
    } catch (error) {
      toast.error('Error al cargar órdenes');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id, filtroEstado]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filtrar en frontend
  const ordenesFiltradas = ordenes.filter(o => {
    if (filtroNumero && !o.numero.toLowerCase().includes(filtroNumero.toLowerCase())) return false;
    if (filtroProveedor && !(o.proveedor_nombre || '').toLowerCase().includes(filtroProveedor.toLowerCase())) return false;
    return true;
  });

  const handleDelete = (orden) => {
    setOrdenToDelete(orden);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!ordenToDelete) return;
    setDeleting(true);
    try {
      await deleteOrdenCompra(ordenToDelete.id);
      toast.success('Orden eliminada');
      setDeleteOpen(false);
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadPDF = async (orden) => {
    try {
      const response = await api.get(`/ordenes-compra/${orden.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `OC_${orden.numero}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF descargado');
    } catch (error) {
      toast.error('Error al descargar PDF');
    }
  };

  // Crear factura desde OC
  const handleCrearFactura = (orden) => {
    setOrdenToFactura(orden);
    setFacturaData({ 
      fecha: new Date().toISOString().split('T')[0], 
      serie: 'F001', 
      nro: '', 
      tc: 1,
      nota: orden.observaciones || ''
    });
    setFacturaOpen(true);
  };

  const confirmCrearFactura = async () => {
    if (!ordenToFactura) return;
    if (!facturaData.nro) {
      toast.error('El número de factura es obligatorio');
      return;
    }
    
    setCreatingFactura(true);
    try {
      const res = await api.post(`/compras/desde-orden/${ordenToFactura.id}`, {
        fecha: facturaData.fecha,
        serie: facturaData.serie,
        nro: facturaData.nro,
        nro_doc_full: `${facturaData.serie}-${facturaData.nro}`,
        tc: facturaData.tc,
        nota: facturaData.nota
      });
      toast.success(`Factura creada: ${res.data.mensaje}`);
      setFacturaOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear factura');
    } finally {
      setCreatingFactura(false);
    }
  };

  const totalPendiente = ordenes.filter(o => o.estado === 'PENDIENTE').reduce((sum, o) => sum + o.total, 0);
  const totalCompletada = ordenes.filter(o => o.estado === 'COMPLETADA').reduce((sum, o) => sum + o.total, 0);

  if (!empresaActual) {
    return <div className="text-center py-12 text-gray-500">Selecciona una empresa</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ClipboardList className="w-7 h-7 text-indigo-600" />
            Órdenes de Compra
          </h1>
          <p className="text-gray-500">Gestiona tus órdenes de compra a proveedores</p>
        </div>
        <Button onClick={() => navigate('/finanzas/ordenes-compra/nueva')} className="bg-indigo-600 hover:bg-indigo-700">
          <Plus className="w-4 h-4 mr-2" />
          Nueva Orden
        </Button>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <Label className="text-sm text-gray-600">N° Orden</Label>
            <Input
              placeholder="Buscar por número..."
              value={filtroNumero}
              onChange={(e) => setFiltroNumero(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-sm text-gray-600">Proveedor</Label>
            <Input
              placeholder="Buscar por proveedor..."
              value={filtroProveedor}
              onChange={(e) => setFiltroProveedor(e.target.value)}
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-sm text-gray-600">Estado</Label>
            <Select value={filtroEstado} onValueChange={setFiltroEstado}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="PENDIENTE">Pendiente</SelectItem>
                <SelectItem value="COMPLETADA">Completada</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button 
              variant="outline" 
              onClick={() => {
                setFiltroNumero('');
                setFiltroProveedor('');
                setFiltroEstado('all');
              }}
              className="w-full"
            >
              Limpiar filtros
            </Button>
          </div>
        </div>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-3 gap-4">
        <Card><CardContent className="p-4">
          <p className="text-sm text-gray-500">Total Órdenes</p>
          <p className="text-2xl font-bold">{ordenes.length}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-sm text-gray-500">Pendientes</p>
          <p className="text-2xl font-bold text-yellow-600">S/. {totalPendiente.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</p>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <p className="text-sm text-gray-500">Completadas</p>
          <p className="text-2xl font-bold text-emerald-600">S/. {totalCompletada.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</p>
        </CardContent></Card>
      </div>

      {/* Tabla */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>N° Orden</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>Proveedor</TableHead>
                <TableHead>Condición</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">Cargando...</TableCell></TableRow>
              ) : ordenesFiltradas.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-gray-500">No hay órdenes de compra</TableCell></TableRow>
              ) : ordenesFiltradas.map(orden => (
                <TableRow key={orden.id} className="hover:bg-gray-50">
                  <TableCell className="font-medium font-mono">{orden.numero}</TableCell>
                  <TableCell>{orden.fecha}</TableCell>
                  <TableCell>{orden.proveedor_nombre || '-'}</TableCell>
                  <TableCell className="text-sm">{orden.condicion_pago}{orden.dias_credito > 0 ? ` (${orden.dias_credito}d)` : ''}</TableCell>
                  <TableCell className="text-right font-medium">
                    {orden.moneda_codigo} {orden.total?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell><Badge className={ESTADO_COLORS[orden.estado]}>{orden.estado}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleDownloadPDF(orden)} title="Descargar PDF" className="text-indigo-600">
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/finanzas/ordenes-compra/${orden.id}`)} title="Ver/Editar" className="text-blue-600">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {!orden.facturada && orden.estado !== 'CANCELADA' && (
                        <Button variant="ghost" size="sm" onClick={() => handleCrearFactura(orden)} title="Crear Factura" className="text-emerald-600">
                          <Receipt className="w-4 h-4" />
                        </Button>
                      )}
                      {orden.estado === 'PENDIENTE' && (
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(orden)} title="Eliminar" className="text-red-500">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Modal Eliminar */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Eliminar Orden
            </DialogTitle>
          </DialogHeader>
          {ordenToDelete && (
            <div className="space-y-4">
              <p>¿Estás seguro de eliminar la orden <strong>{ordenToDelete.numero}</strong>?</p>
              <div className="bg-gray-50 rounded p-3">
                <p className="text-sm text-gray-600">Proveedor: {ordenToDelete.proveedor_nombre}</p>
                <p className="text-sm text-gray-600">Total: {ordenToDelete.moneda_codigo} {ordenToDelete.total?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancelar</Button>
            <Button onClick={confirmDelete} disabled={deleting} className="bg-red-600 hover:bg-red-700">
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Crear Factura desde OC */}
      <Dialog open={facturaOpen} onOpenChange={setFacturaOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-600">
              <Receipt className="w-5 h-5" />
              Crear Factura desde Orden de Compra
            </DialogTitle>
          </DialogHeader>
          {ordenToFactura && (
            <div className="space-y-4">
              <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                <p className="text-sm font-medium text-emerald-800">Orden: {ordenToFactura.numero}</p>
                <p className="text-sm text-emerald-700">Proveedor: {ordenToFactura.proveedor_nombre}</p>
                <p className="text-sm text-emerald-700">Total: {ordenToFactura.moneda_codigo} {ordenToFactura.total?.toLocaleString('es-PE', { minimumFractionDigits: 2 })}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Fecha Factura *</Label>
                  <Input 
                    type="date" 
                    value={facturaData.fecha} 
                    onChange={(e) => setFacturaData({...facturaData, fecha: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Tipo de Cambio</Label>
                  <Input 
                    type="number" 
                    step="0.001"
                    value={facturaData.tc} 
                    onChange={(e) => setFacturaData({...facturaData, tc: parseFloat(e.target.value) || 1})}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Serie</Label>
                  <Input 
                    value={facturaData.serie} 
                    onChange={(e) => setFacturaData({...facturaData, serie: e.target.value.toUpperCase()})}
                    placeholder="F001"
                  />
                </div>
                <div>
                  <Label>Número *</Label>
                  <Input 
                    value={facturaData.nro} 
                    onChange={(e) => setFacturaData({...facturaData, nro: e.target.value})}
                    placeholder="00001234"
                  />
                </div>
              </div>

              <p className="text-xs text-gray-500">
                Se creará una factura con los mismos artículos, cantidades y precios de la orden de compra.
                También se generará automáticamente la cuenta por pagar (CxP).
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFacturaOpen(false)}>Cancelar</Button>
            <Button onClick={confirmCrearFactura} disabled={creatingFactura} className="bg-emerald-600 hover:bg-emerald-700">
              {creatingFactura ? 'Creando...' : 'Crear Factura'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdenesCompraPage;
